import { Phone } from "../types/global/types";

/**
 * Helpers for the fixed two-slot phone editor (Cell + Work). A contact's `phones`
 * array may also hold a household house phone (type "Home") that is edited elsewhere;
 * these helpers must never disturb non-Cell/Work entries.
 */

export type PhoneSlot = "Mobile" | "Work" | "Home";

// A phone fills the Cell slot when its type is Mobile or blank (legacy untyped numbers).
export const isCellPhone = (phone: Phone): boolean => phone.type === "Mobile" || phone.type === "";

export const isWorkPhone = (phone: Phone): boolean => phone.type === "Work";

// The household house phone is a Home-type phone (one per household).
export const isHousePhone = (phone: Phone): boolean => phone.type === "Home";

export const findCellPhone = (phones: Phone[]): Phone | undefined => phones.find(isCellPhone);
export const findWorkPhone = (phones: Phone[]): Phone | undefined => phones.find(isWorkPhone);
export const findHousePhone = (phones: Phone[]): Phone | undefined => phones.find(isHousePhone);

/**
 * Return a new phones array with the given fixed slot set to `value`:
 *  - empty value removes the slot's phone (so no empty phone is ever submitted),
 *  - an existing slot phone is updated in place,
 *  - otherwise a new phone is appended.
 * All other phones are preserved untouched.
 */
export const setPhoneSlot = (phones: Phone[], slotType: PhoneSlot, value: string): Phone[] => {
  const next = [...phones];
  const matcher = slotType === "Mobile" ? isCellPhone : slotType === "Work" ? isWorkPhone : isHousePhone;
  const idx = next.findIndex(matcher);

  if (value.trim() === "") {
    if (idx >= 0) next.splice(idx, 1);
  } else if (idx >= 0) {
    next[idx] = { ...next[idx], number: value, type: slotType };
  } else {
    next.push({ number: value, type: slotType, area_code: "", country: "" });
  }

  return next;
};

/**
 * Return a new phones array with the Work-slot phone's `extension` set to `value`.
 * The extension only makes sense alongside a work number, so:
 *  - if a Work phone exists, its extension is updated (empty clears it),
 *  - if no Work phone exists, the array is returned unchanged (no empty phone created).
 * Other phones are preserved untouched.
 */
export const setPhoneExtension = (phones: Phone[], value: string): Phone[] => {
  const idx = phones.findIndex(isWorkPhone);
  if (idx < 0) return phones;

  const next = [...phones];
  next[idx] = { ...next[idx], extension: value.trim() === "" ? "" : value };
  return next;
};

// Read the Work-slot extension (empty string when absent).
export const findWorkExtension = (phones: Phone[]): string => findWorkPhone(phones)?.extension || "";
